import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Form, Input, Button, message, Checkbox } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { register } from './RegisterServices';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import CommonWrapper from '../Shared/CommonWrapper/CommonWrapper';

const { Text } = Typography;

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, setLoading } = useGlobal();
  const { handleError } = useErrorHandler();

  const onFinish = useCallback(async (values) => {
    try {
      setLoading(true);
      const { confirmPassword, ...userData } = values;
      await register(userData);
      message.success(t('registration_successful_you_can_now_log_in'));
      navigate('/login');
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [navigate, setLoading, handleError]);

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  return (
    <CommonWrapper>
      <Form
        name="register"
        style={{ maxWidth: '100%', margin: '0 auto' }}
        onFinish={onFinish}
      >
        <Form.Item
          name="first_name"
          rules={[{ required: true, message: t('please_input_your_first_name') }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('first_name')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        <Form.Item
          name="last_name"
          rules={[{ required: true, message: t('please_input_your_last_name') }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('last_name')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: t('please_input_your_email') },
            { type: 'email', message: t('please_enter_a_valid_email') },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder={t('email_address')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            { required: true, message: t('please_input_your_password') },
            { min: 6, message: t('password_must_be_at_least_6_characters_long') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('password')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, message: t('please_confirm_your_password') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('the_two_passwords_do_not_match')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('confirm_password')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>
        <Form.Item
          name="privacy_policy_accepted"
          valuePropName="checked"
          style={{ marginBottom: 0 }}
          rules={[{
            required: true,
            message: t('you_must_accept_the_privacy_policy'),
            validator: (_, value) =>
              value ? Promise.resolve() : Promise.reject(new Error(t('you_must_accept_the_terms_of_service')))
          }]}
        >
          <Checkbox>
            <Text strong>
              {t('i_agree_to_the')}{' '}
              <a
                href="/terms-of-service"
                target="_blank"
                style={{ color: '#1890ff' }}
              >
                {t('terms_of_service')}
              </a>
            </Text>
          </Checkbox>
        </Form.Item>
        <Form.Item style={{ marginBottom: 10 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            style={{ width: '100%', borderRadius: '5px' }}
          >
            {t('register')}
          </Button>
        </Form.Item>

        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <Text>{t('already_have_an_account')} </Text>
          <Button
            type="link"
            style={{ fontSize: '0.9em', padding: 0 }}
            onClick={handleLoginRedirect}
          >
            {t('log_in')}
          </Button>
        </div>
      </Form>
    </CommonWrapper>
  );
};

export default Register;
